/*
 * Get the number of days in month regardless of whether the year is provided or not
 * @param {number} params.month - Zero-based month index (optional)
 * @param {number} params.year - Zero-based month index (optional)
 */
export function getDaysInMonth({
  month,
  year,
}: {
  month: number
  year?: number
}): number {
  // month or year, when unset, can be either undefined, or -1
  const normalizedMonth =
    month === undefined || month === -1 ? undefined : month
  const normalizedYear = year === undefined || year === -1 ? undefined : year

  if (normalizedYear && normalizedMonth !== undefined) {
    return daysInMonthOfYear(normalizedMonth, normalizedYear)
  } else if (normalizedMonth !== undefined) {
    return daysInMonth(normalizedMonth)
  } else {
    return 31
  }
}

/*
 * Check the object containing day, month and year and
 * prevent invalid dates like February 30 or April 31
 */
export function getValidDate({ day, month, year }) {
  const maxNumOfDays = getDaysInMonth({ month, year })

  if (day && day > maxNumOfDays) {
    day = maxNumOfDays
  }

  return { day, month, year }
}

/*
 * Naive approach to getting max number of days in a month
 * (requires only month index; does not consider leap years)
 * @param {number} month - Zero-based month index
 */
function daysInMonth(month) {
  const maxDaysInMonths = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return maxDaysInMonths[month]
}

/*
 * From Stack Overflow:
 * (http://stackoverflow.com/questions/1184334/get-number-days-in-a-specified-month-using-javascript)
 *
 * Returns number of days in any month of a given year (accounting for leap years)
 * EXPLANATION (because this function, while elegant, may be confusing):
 * - the Date constructor takes year, month, and a number of optional arguments,
 *   the first of which is the day
 * - months are counted starting from 0, while days start with 1
 *   (e.g. to represent January 1, 2000, we need to call new Date(2000, 0, 1))
 * - passing 0 as the day means that the constructor will instead use the last day
 *   of the previous month (and .getDate will show which day it is,
 *   i.e. how many days there are in this month)
 */
function daysInMonthOfYear(month, year) {
  const nextMonth = month + 1

  // get the last day (same as number of days) of the month preceding the next month
  // (i.e. of the month passed to the function)
  return new Date(year, nextMonth, 0).getDate()
}

/*
 * Just concat year, month and day and get the date string (in YYYY-MM-DD format)
 * Why? Because doing it through the Date constructor requires dealing with timezones
 */
export function buildDateShortISOString(year, month, day) {
  month += 1 // correct for the fact that month is zero-based
  month = month < 10 ? `0${month}` : `${month}`
  day = day < 10 ? `0${day}` : `${day}`
  return `${year}-${month}-${day}`
}

export function getTodayDate(): string {
  const date = new Date()
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
}

/*
 * Introduced following the FRNT-1116 bug.
 * Safari cannot create a `new Date` object from string like "2021-1-20".
 * Safari requires the months to be a two-digits number.
 */
export function buildStandardDate(date: string): string {
  return date
    ?.split('-')
    .map(number => number.padStart(2, '0'))
    .join('-')
}

export const getPublicationYear = (date: number | null): number | null =>
  date ? new Date(date * 1000).getFullYear() : null
